/**
 * HeadlinesPage
 *
 * Main page for internal team announcements.
 * Click a row to view/edit in a right-side draggable panel.
 * Any team member can create/edit headlines for their teams; Leadership
 * super-team members can manage every team's headlines.
 */

import { useState, useMemo, useCallback, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Plus, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useCanManageInTeams, useCanManageItemTeams } from '@/hooks/useSession';
import { useDebounce } from '@/hooks/useDebounce';
import { SlidePanel } from '@/shared/components';
import { AttachmentsList } from '@/features/attachments';
import { useHeadlines } from '../hooks/useHeadlines';
import { useTeams } from '@/features/rocks/hooks/useTeams';
import { HeadlinesTable } from '../components/HeadlinesTable';
import { HeadlineForm } from '../components/HeadlineForm';
import type { Headline, HeadlineFilters, HeadlineOwner } from '../types';

type ActiveTab = 'current' | 'upcoming' | 'past' | 'guide';

const TABS: { value: ActiveTab; label: string; adminOnly?: boolean }[] = [
  { value: 'current', label: 'Current' },
  { value: 'upcoming', label: 'Upcoming', adminOnly: true },
  { value: 'past', label: 'Past' },
  { value: 'guide', label: 'Guide' },
];

const PANEL_STORAGE_KEY = 'atek_headline_panel_width';
const ACTIVE_TAB_STORAGE_KEY = 'atek_headline_active_tab';
const TEAM_FILTER_STORAGE_KEY = 'atek_headline_team_filter';
const OWNER_FILTER_STORAGE_KEY = 'atek_headline_owner_filter';

const VALID_TABS: ActiveTab[] = ['current', 'upcoming', 'past', 'guide'];

const readStoredIds = (key: string): string[] => {
  try {
    const saved = localStorage.getItem(key);
    return saved ? JSON.parse(saved) as string[] : [];
  } catch {
    return [];
  }
};

export function HeadlinesPage() {
  // canManage = user is on at least one team (or Leadership). Per-row edit
  // permission is checked separately in HeadlinesTable using item.team_ids.
  const canManage = useCanManageInTeams();

  const [activeTab, setActiveTab] = useState<ActiveTab>(() => {
    const saved = localStorage.getItem(ACTIVE_TAB_STORAGE_KEY) as ActiveTab | null;
    return saved && VALID_TABS.includes(saved) ? saved : 'current';
  });

  const [searchInput, setSearchInput] = useState('');
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>(() => readStoredIds(TEAM_FILTER_STORAGE_KEY));
  const [selectedOwnerIds, setSelectedOwnerIds] = useState<string[]>(() => readStoredIds(OWNER_FILTER_STORAGE_KEY));
  const includeArchived = activeTab === 'past';

  useEffect(() => {
    localStorage.setItem(ACTIVE_TAB_STORAGE_KEY, activeTab);
  }, [activeTab]);

  useEffect(() => {
    localStorage.setItem(TEAM_FILTER_STORAGE_KEY, JSON.stringify(selectedTeamIds));
  }, [selectedTeamIds]);

  useEffect(() => {
    localStorage.setItem(OWNER_FILTER_STORAGE_KEY, JSON.stringify(selectedOwnerIds));
  }, [selectedOwnerIds]);

  // Right-side panel state (used for both view/edit and create)
  const [selectedHeadline, setSelectedHeadline] = useState<Headline | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const isCreating = panelOpen && !selectedHeadline;
  const canEditSelected = useCanManageItemTeams(selectedHeadline?.team_ids ?? []);

  const debouncedSearch = useDebounce(searchInput, 300);
  const { data: teams = [] } = useTeams();

  const filters: HeadlineFilters = useMemo(() => ({
    search: debouncedSearch || undefined,
    team_ids: selectedTeamIds.length > 0 ? selectedTeamIds : undefined,
    include_archived: includeArchived || undefined,
    scheduled:
      activeTab === 'upcoming' ? 'upcoming'
        : activeTab === 'past' ? 'all'
          : 'published',
    page_length: 100,
  }), [debouncedSearch, selectedTeamIds, includeArchived, activeTab]);

  const { data, isLoading } = useHeadlines(filters);

  const allHeadlines = useMemo(() => data?.headlines || [], [data]);

  const owners = useMemo((): HeadlineOwner[] => {
    const seen = new Map<string, string>();
    for (const h of allHeadlines) {
      if (h.owner_id && h.owner_name && !seen.has(h.owner_id)) {
        seen.set(h.owner_id, h.owner_name);
      }
    }
    return Array.from(seen.entries()).map(([id, name]) => ({ id, name }));
  }, [allHeadlines]);

  const headlines = useMemo(() => {
    let rows = allHeadlines;
    const now = Date.now();
    if (activeTab === 'past') {
      rows = rows.filter((h) => h.is_archived);
    } else if (activeTab === 'upcoming') {
      rows = rows.filter((h) => !h.is_archived && h.scheduled_at && new Date(h.scheduled_at).getTime() > now);
    } else {
      // Current: not archived, and either published immediately (no scheduled_at
      // or already past it). Double-check client-side so stale cache never
      // leaks an upcoming headline into the current list.
      rows = rows.filter(
        (h) => !h.is_archived
          && (!h.scheduled_at || new Date(h.scheduled_at).getTime() <= now),
      );
    }
    if (selectedOwnerIds.length > 0) {
      rows = rows.filter((h) => h.owner_id && selectedOwnerIds.includes(h.owner_id));
    }
    return rows;
  }, [allHeadlines, selectedOwnerIds, activeTab]);

  const handleSelectHeadline = useCallback((headline: Headline) => {
    setSelectedHeadline(headline);
    setPanelOpen(true);
  }, []);

  const handleCreateHeadline = useCallback(() => {
    setSelectedHeadline(null);
    setPanelOpen(true);
  }, []);

  const handleClosePanel = useCallback(() => {
    setSelectedHeadline(null);
    setPanelOpen(false);
  }, []);

  // Keep selected headline in sync with data refreshes
  useEffect(() => {
    if (selectedHeadline && data?.headlines) {
      const updated = data.headlines.find((h) => h.id === selectedHeadline.id);
      if (updated) {
        setSelectedHeadline(updated);
      }
    }
  }, [data, selectedHeadline?.id]);

  return (
    <div className="space-y-4">
      {/* Tab bar + toolbar (compact single row, matching KPI/Rocks layout) */}
      <div className="flex items-center justify-between gap-2 border-b border-border flex-wrap">
        <div className="flex items-center gap-0">
          {TABS.filter((tab) => !tab.adminOnly || canManage).map((tab) => (
            <button
              key={tab.value}
              onClick={() => setActiveTab(tab.value)}
              className={cn(
                'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
                activeTab === tab.value
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border',
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {canManage && (activeTab === 'current' || activeTab === 'upcoming') && (
          <div className="flex items-center gap-2 flex-wrap pb-1.5">
            <Button size="sm" onClick={handleCreateHeadline}>
              <Plus className="h-4 w-4 mr-1" />
              New Headline
            </Button>
          </div>
        )}
      </div>

      {/* Main content area with fixed slide-in panel */}
      <div className="flex min-h-[calc(100vh-240px)]">
        {/* Left: table content */}
        <div className="flex-1 min-w-0 overflow-auto">
          {activeTab === 'guide' ? (
            <HeadlinesGuide />
          ) : isLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-[200px] w-full rounded-lg" />
              <Skeleton className="h-[300px] w-full rounded-lg" />
            </div>
          ) : (
            <HeadlinesTable
              headlines={headlines}
              count={headlines.length}
              emptyMessage={
                activeTab === 'past'
                  ? 'No past headlines.'
                  : activeTab === 'upcoming'
                    ? 'No upcoming headlines scheduled.'
                    : 'No headlines yet. Create one to share team announcements.'
              }
              canManage={canManage}
              teams={teams}
              onSelectHeadline={handleSelectHeadline}
              onCreateHeadline={activeTab === 'current' ? handleCreateHeadline : undefined}
              selectedHeadlineId={selectedHeadline?.id}
              searchInput={searchInput}
              onSearchChange={setSearchInput}
              selectedTeamIds={selectedTeamIds}
              onSelectedTeamIdsChange={setSelectedTeamIds}
              owners={owners}
              selectedOwnerIds={selectedOwnerIds}
              onSelectedOwnerIdsChange={setSelectedOwnerIds}
            />
          )}
        </div>

        <SlidePanel
          open={panelOpen && !!(selectedHeadline || isCreating)}
          storageKey={PANEL_STORAGE_KEY}
        >
          <div className="flex items-center justify-between px-4 py-3 border-b shrink-0">
            <span className="text-sm font-semibold truncate">
              {isCreating ? 'New Headline' : canEditSelected ? 'Edit Headline' : 'View Headline'}
            </span>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleClosePanel}>
              <X className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-4">
            {isCreating || canEditSelected ? (
              <HeadlineForm
                key={selectedHeadline?.id ?? 'new'}
                open={panelOpen}
                onClose={handleClosePanel}
                headline={selectedHeadline}
                teams={teams}
              />
            ) : selectedHeadline ? (
              <div className="space-y-4">
                <div>
                  <Label className="text-xs text-muted-foreground">Title</Label>
                  <p className="text-sm font-medium mt-1">{selectedHeadline.title}</p>
                </div>
                {selectedHeadline.body && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Details</Label>
                    <p className="text-sm mt-1 whitespace-pre-wrap">{selectedHeadline.body}</p>
                  </div>
                )}
                <div>
                  <Label className="text-xs text-muted-foreground">
                    {selectedHeadline.team_ids.length > 1 ? 'Teams' : 'Team'}
                  </Label>
                  <p className="text-sm mt-1">
                    {(() => {
                      const names = (selectedHeadline.team_ids ?? [])
                        .map((id) => teams.find((t) => t.id === id)?.name)
                        .filter((n): n is string => !!n);
                      return names.length > 0 ? names.join(', ') : '—';
                    })()}
                  </p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Owner</Label>
                  <p className="text-sm mt-1">{selectedHeadline.owner_name || '—'}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Attachments</Label>
                  <div className="mt-1">
                    <AttachmentsList
                      entityType="headline"
                      entityId={selectedHeadline.id}
                      readOnly
                      emptyText="No documents attached."
                    />
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </SlidePanel>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Guide tab content
// ─────────────────────────────────────────────────────────────────────────────

function HeadlinesGuide() {
  return (
    <div className="rounded-lg border bg-card p-6 space-y-6 text-sm max-w-3xl">
      <div>
        <h2 className="text-lg font-semibold">What is a Headline?</h2>
        <p className="text-muted-foreground mt-2">
          A Headline is a short internal team announcement — a company, team, or
          operational update that everyone on the selected teams should be aware of.
          Think of it as the <em>"employee & customer headline"</em> section from an
          EOS L10 meeting, surfaced here so nothing lives only in chat or email.
        </p>
      </div>

      <div>
        <h3 className="font-semibold">When to use one</h3>
        <ul className="list-disc pl-5 mt-2 space-y-1 text-muted-foreground">
          <li>Kudos, wins, or milestones worth recognising across a team.</li>
          <li>Customer news: a new account, a churn, a notable renewal.</li>
          <li>People news: new hires, role changes, departures, PTO alerts.</li>
          <li>Ops news: a launch, an outage recap, a policy change.</li>
        </ul>
      </div>

      <div>
        <h3 className="font-semibold">What is NOT a headline</h3>
        <ul className="list-disc pl-5 mt-2 space-y-1 text-muted-foreground">
          <li>
            <strong>A to-do or action item</strong> — if someone needs to do something,
            create a To-Do, not a headline. Headlines are for awareness, not work.
          </li>
          <li>
            <strong>An issue, blocker, or complaint</strong> — that's for the Issues
            List or the L10 discussion section.
          </li>
          <li>
            <strong>A quarterly goal</strong> — that's a Rock. Rocks have owners, due
            dates, deliverables. Headlines don't.
          </li>
          <li>
            <strong>A KPI / metric update</strong> — those live in the scorecard and
            refresh automatically.
          </li>
          <li>
            <strong>A private 1:1 message</strong> — DM the person; don't broadcast.
          </li>
          <li>
            <strong>A long-form doc or policy</strong> — link to the doc from a short
            headline instead of pasting the whole thing in Details.
          </li>
          <li>
            <strong>Anything urgent / blocking</strong> — use chat or call the person.
            Headlines are read asynchronously, often in the weekly meeting.
          </li>
        </ul>
      </div>

      <div>
        <h3 className="font-semibold">Tabs</h3>
        <ul className="list-disc pl-5 mt-2 space-y-1 text-muted-foreground">
          <li><strong>Current</strong> — headlines published now, visible to team members.</li>
          <li><strong>Upcoming</strong> (admin) — scheduled for a future date. Hidden from non-admin viewers until their publish time.</li>
          <li><strong>Past</strong> — archived headlines. Restore from the row menu.</li>
          <li><strong>Guide</strong> — this page.</li>
        </ul>
      </div>

      <div>
        <h3 className="font-semibold">Scheduling</h3>
        <p className="text-muted-foreground mt-2">
          <em>Publish at</em> defaults to the current time — click Create and it goes
          out immediately. Push the date into the future to make it an upcoming
          headline; it stays hidden from non-admin viewers until the scheduled time,
          then appears in <em>Current</em> automatically.
        </p>
      </div>

      <div>
        <h3 className="font-semibold">Tips</h3>
        <ul className="list-disc pl-5 mt-2 space-y-1 text-muted-foreground">
          <li>Pick <strong>Teams</strong> carefully — non-members won't see the headline.</li>
          <li>The <strong>Owner</strong> defaults to you — reassign if it's someone else's news.</li>
          <li>Attach files (PDFs, screenshots) when context matters.</li>
          <li>Keep titles short and scannable; put the "why" in Details.</li>
        </ul>
      </div>
    </div>
  );
}
